import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, MotionConfig } from "motion/react";
import { useStore } from "zustand";
import { isLive, runningEntry, type SyncOperation } from "@otrack/domain";
import { Button, Icon, IconButton, OTrackMark } from "@otrack/ui";
import { ClientEditor } from "./client-editor";
import { CommandBar } from "./command-bar";
import { CommandPalette } from "./command-palette";
import { useDesktopIntegration } from "./desktop";
import { initialSyncStatus } from "@otrack/local-store";
import { AppContext, type AppContextValue, type CommandRequest, type Services, type Toast } from "./context";
import { EntryEditor } from "./entry-editor";
import { ImportDialog } from "./import-dialog";
import { NewInvoice } from "./new-invoice";
import { MonthStrip } from "./month-strip";
import { useGoogleSync } from "./calendar/google";
import { invertChanges } from "./calendar/google-events";
import { CalendarPage } from "./calendar/page";
import { ClientPage } from "./pages/client";
import { InvoicePage } from "./pages/invoice";
import { InvoicesPage } from "./pages/invoices";
import { ProjectPage } from "./pages/project";
import { ProjectsPage } from "./pages/projects";
import { SettingsPage } from "./pages/settings";
import { TrashPage } from "./pages/trash";
import { ProjectEditor } from "./project-editor";
import { LiveTimer, TimerCard, TodayCard } from "./status-cards";
import { modifierKey } from "./palette";
import { href, navigate, useRoute } from "./router";
import type { AppStore } from "./store";
import { Strata } from "./strata";
import { formatDay } from "./time";
import { SectionNav, type Section } from "./section-nav";
import { SyncBadge } from "./sync-badge";
import { UpdatePill } from "./update-pill";
import { useNow } from "./use-now";

const RAIL: Section[] = [
  { label: "Today", icon: "house", route: { page: "today" }, pages: ["today"] },
  { label: "Calendar", icon: "calendar", route: { page: "calendar", view: null, date: null }, pages: ["calendar"] },
  { label: "Projects", icon: "folder", route: { page: "projects" }, pages: ["projects", "project", "client"] },
  { label: "Invoices", icon: "receipt", route: { page: "invoices" }, pages: ["invoices", "invoice"] },
  { label: "Settings", icon: "settings", route: { page: "settings" }, pages: ["settings", "trash"] }
];

function useEffectiveTheme(preference: "light" | "dark" | "system"): "light" | "dark" {
  const query = typeof window === "undefined" ? null : window.matchMedia("(prefers-color-scheme: dark)");
  const [systemDark, setSystemDark] = useState(query?.matches ?? false);
  useEffect(() => {
    if (!query) return;
    const listener = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    query.addEventListener("change", listener);
    return () => query.removeEventListener("change", listener);
  }, [query]);
  return preference === "system" ? (systemDark ? "dark" : "light") : preference;
}

const LOCAL_ONLY: Services = { sync: { mode: "local", status: initialSyncStatus, account: null }, assistant: null };

export function OTrackApp({ store, services = LOCAL_ONLY }: { store: AppStore; services?: Services }) {
  const now = useNow(1000);
  const dataset = useStore(store, (state) => state.dataset);
  const theme = useEffectiveTheme(dataset.settings.theme);
  const [toast, setToast] = useState<Toast | null>(null);
  const [entryId, setEntryId] = useState<string | null>(null);
  // The Client to preselect, null for none, or false when the dialog is closed.
  const [newProject, setNewProject] = useState<string | null | false>(false);
  const [newClient, setNewClient] = useState(false);
  const [newInvoice, setNewInvoice] = useState<string | null | false>(false);
  const route = useRoute();
  const [importing, setImporting] = useState(false);
  const [palette, setPalette] = useState(false);
  const [commandRequest, setCommandRequest] = useState<CommandRequest | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    document.documentElement.dataset["theme"] = theme;
  }, [theme]);

  // Calendar Feeds are gone (ADR 0027): their cached events have nothing left to show.
  useEffect(() => {
    try {
      for (const key of Object.keys(localStorage)) if (key.startsWith("otrack:ics:")) localStorage.removeItem(key);
    } catch { /* Storage may be off; nothing to clean then. */ }
  }, []);

  const show = useCallback((message: string, undoable: boolean) => {
    window.clearTimeout(toastTimer.current);
    setToast({ id: Date.now(), message, undoable });
    toastTimer.current = window.setTimeout(() => setToast(null), 6000);
  }, []);

  const notify = useCallback((message: string) => show(message, false), [show]);
  const google = useGoogleSync(services.google, dataset.settings, notify);

  const undo = useCallback(async () => {
    if (await store.getState().undo()) show("Undone", false);
  }, [store, show]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPalette((open) => !open);
        return;
      }
      const typing = (event.target as HTMLElement | null)?.closest("input, textarea, select, [contenteditable='true']");
      if (!typing && (event.ctrlKey || event.metaKey) && !event.shiftKey && event.key.toLowerCase() === "z") {
        event.preventDefault();
        void undo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo]);

  const context = useMemo<AppContextValue>(() => ({
    store,
    now,
    services,
    async act(operations: SyncOperation[], message: string, options = {}) {
      const changes = google && options.google?.length ? options.google : null;
      if (changes) google!.enqueue(changes);
      await store.getState().dispatch(operations, { ...(options.undoable === false ? { undoable: false } : {}), ...(changes ? { undoExtra: () => google!.enqueue(invertChanges(changes)) } : {}) });
      show(message, options.undoable !== false);
    },
    notify,
    google,
    openEntry: setEntryId,
    openNewProject: (clientId?: string) => setNewProject(clientId ?? null),
    openNewClient: () => setNewClient(true),
    openNewInvoice: (clientId?: string) => setNewInvoice(clientId ?? null),
    openImport: () => setImporting(true),
    openPalette: () => setPalette(true),
    commandRequest,
    sendToCommandBar(text: string, run: boolean) {
      setCommandRequest({ id: Date.now(), text, run });
      navigate({ page: "today" });
    },
    clearCommandRequest: () => setCommandRequest(null)
  }), [store, now, services, show, notify, google, commandRequest]);

  useDesktopIntegration(dataset, context);

  const toggleTheme = useCallback(() => {
    const next = theme === "dark" ? "light" : "dark";
    void store.getState().dispatch([store.getState().factory.op("settings", "settings", { theme: next })], { undoable: false });
  }, [store, theme]);
  const closePalette = useCallback(() => setPalette(false), []);
  const undoFromPalette = useCallback(() => void undo(), [undo]);

  const hasProjects = Object.values(dataset.projects).some(isLive);
  const running = runningEntry(dataset) !== null;
  // Paging through the Calendar keeps the page mounted; other pages enter fresh.
  const routeKey = "id" in route ? `${route.page}:${route.id}` : route.page;

  return <AppContext.Provider value={context}>
    <MotionConfig reducedMotion="user" transition={{ type: "spring", stiffness: 420, damping: 38 }}>
    <div className="ot-app">
      <header className="ot-top">
        <div className="ot-island">
          <a className="ot-brand" href={href({ page: "today" })} aria-label="ontrack, go to Today">
            <OTrackMark size={24} running={running}/>
            <span className="ot-brand__name">ontrack</span>
          </a>
          <i className="ot-island__rule" aria-hidden="true"/>
          <SectionNav sections={RAIL} page={route.page}/>
          <i className="ot-island__rule" aria-hidden="true"/>
          <LiveTimer/>
          <div className="ot-top__end">
            <UpdatePill/>
            <SyncBadge/>
            <IconButton className="is-ghost" label="Search" icon="search" onClick={() => setPalette(true)} aria-keyshortcuts="Control+K Meta+K" tooltipShortcut={`${modifierKey()} K`} tooltipPlacement="bottom"/>
            <IconButton className="is-ghost" label={theme === "dark" ? "Light theme" : "Dark theme"} icon={theme === "dark" ? "sun" : "moon"} onClick={toggleTheme} tooltipPlacement="bottom"/>
          </div>
        </div>
      </header>
      <motion.main
        id="main"
        key={routeKey}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
      >
        {route.page === "today" && <div className="ot-today">
          <h1 className="ot-today__date">{formatDay(now)}</h1>
          <CommandBar/>
          <div className="ot-status"><TimerCard/><TodayCard/></div>
          {hasProjects ? <><Strata/><MonthStrip/></> : <section className="ot-welcome">
            <OTrackMark size={44}/>
            <div className="ot-welcome__text">
              <h2>Bring your work in</h2>
              <p>Import your solidtime history so your earnings carry over, or start fresh with a project.</p>
            </div>
            <div className="ot-welcome__actions">
              <Button tone="primary" icon="download" onClick={() => setImporting(true)}>Import from solidtime</Button>
              <Button icon="plus" onClick={() => setNewProject(null)}>Create a project</Button>
            </div>
          </section>}
        </div>}
        {route.page === "calendar" && <CalendarPage view={route.view} date={route.date}/>}
        {route.page === "projects" && <ProjectsPage/>}
        {route.page === "project" && <ProjectPage id={route.id}/>}
        {route.page === "client" && <ClientPage id={route.id}/>}
        {route.page === "invoices" && <InvoicesPage/>}
        {route.page === "invoice" && <InvoicePage id={route.id}/>}
        {route.page === "settings" && <SettingsPage/>}
        {route.page === "trash" && <TrashPage/>}
      </motion.main>
      <div className="ot-toast-region" role="status" aria-live="polite">
        <AnimatePresence>
          {toast && <motion.div
            className="ot-toast"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98, transition: { duration: 0.18 } }}
          >
            {toast.undoable && <Icon name="check" weight="bold" className="ot-toast__icon"/>}
            <motion.span key={toast.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>{toast.message}</motion.span>
            {toast.undoable && <button type="button" onClick={() => void undo()}><Icon name="undo" weight="bold"/>Undo</button>}
          </motion.div>}
        </AnimatePresence>
      </div>
      <AnimatePresence>
        {entryId && <EntryEditor key={`entry:${entryId}`} entryId={entryId} onClose={() => setEntryId(null)}/>}
        {newProject !== false && <ProjectEditor key="project" clientId={newProject} onClose={() => setNewProject(false)}/>}
        {newClient && <ClientEditor key="client" onClose={() => setNewClient(false)}/>}
        {newInvoice !== false && <NewInvoice key="invoice" clientId={newInvoice} onClose={() => setNewInvoice(false)}/>}
        {importing && <ImportDialog key="import" onClose={() => setImporting(false)}/>}
        {palette && <CommandPalette key="palette" onClose={closePalette} theme={theme} toggleTheme={toggleTheme} undo={undoFromPalette}/>}
      </AnimatePresence>
    </div>
    </MotionConfig>
  </AppContext.Provider>;
}
